import { type BaseEvent, type EventProducer } from '@atlassian/state-machine/types';

import { encodeTokenForWebSocket } from '@canvas/encoding';

import { WsSocketError } from '../../../../network/errors';
import { registerBrowserSocketCloseHandler } from '../../../../network/register-close-handler';
import { type SocketMachineContext } from '../../../network-state-machine-context';
import { type Events } from '../../../network-state-machine-events';

export type ConnectingContext = Pick<
  SocketMachineContext,
  | 'relayDetails'
  | 'sessionId'
  | 'webSocket'
  | 'traceId'
  | 'socketDisposeListeners'
  | 'signal'
>;

export function asyncProducer<T extends BaseEvent>(
  ctx: { signal: AbortSignal },
  events: EventProducer<T>
) {
  return (event: T) => {
    if (!ctx.signal.aborted) {
      events(event);
    }
  };
}

function createSocketUrl(ctx: ConnectingContext) {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }

  const tracedUrl = new URL(ctx.relayDetails.url);
  tracedUrl.searchParams.set('traceId', ctx.traceId);
  if (ctx.sessionId) {
    tracedUrl.searchParams.set('sessionId', ctx.sessionId);
  }

  return tracedUrl.toString();
}

export function onEnterWebSocket(ctx: ConnectingContext, events: EventProducer<Events>) {
  const producer = asyncProducer(ctx, events);

  function onOpen() {
    producer({
      type: 'open',
    });
  }

  function onMessage(message: MessageEvent) {
    producer({
      type: 'messageReceived',
      message: message.data,
    });
  }

  function onClose(ev: CloseEvent) {
    producer({
      type: 'close',
      error: new WsSocketError(ev),
      code: ev.code,
    });
  }

  function onError(ev: Event) {
    producer({
      type: 'error',
      error: new WsSocketError(ev),
    });
  }

  async function connect() {
    if (!ctx.relayDetails) {
      throw new Error('Missing relay details in context');
    }

    const tracedUrl = createSocketUrl(ctx);

    const webSocket = new WebSocket(
      tracedUrl,
      encodeTokenForWebSocket(ctx.relayDetails.accessToken)
    );
    webSocket.addEventListener('open', onOpen);
    webSocket.addEventListener('close', onClose);
    webSocket.addEventListener('error', onError);
    webSocket.addEventListener('message', onMessage);

    if (globalThis.document) {
      // Register close handler for browser sockets
      registerBrowserSocketCloseHandler(webSocket, ctx.traceId);
    }

    webSocket.binaryType = 'arraybuffer';
    ctx.webSocket = webSocket;
    ctx.socketDisposeListeners = () => {
      webSocket.removeEventListener('open', onOpen);
      webSocket.removeEventListener('close', onClose);
      webSocket.removeEventListener('error', onError);
      webSocket.removeEventListener('message', onMessage);
    };
  }

  connect().catch((error) => {
    producer({
      type: 'error',
      error,
    });
  });
}
